import { useQuery } from '@apollo/client';
import { type FunctionComponent } from 'react';
import { SITE_BASE_URL } from '../../../constants/site';
import { convertToSlug } from '../../../utils/string/url.utils';
import { type ArticleDocumentQuery, type ArticleDocumentQueryVariables } from '../../__generated__/graphql-client-types';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { type ArticlePageType } from '../../components/common-components/page/page-container/page-container.types';
import { ContentComponentHelper } from '../../components/construct-components/render-helper/render.helper';
import { ContentContainer } from '../../components/content-container/content-container.component';
import { FourOhFour } from '../../components/error-components/404/404.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { RedirectWithStatus } from '../../components/redirect-w-status';
import { ARTICLE_PREFIX, LEARNING_CENTER_LINK } from '../../constants/links';
import { getArticleAuthorName, getArticleHeroImageId, getSeoMetaData } from '../../helpers/construct/page-content.helper';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { ARTICLE_PAGE } from '../../queries/article/article.queries';
import { type ArticlePageContent, type ListContent } from '../../types/construct.types';

export type ArticlePageProps = {
	articleId: number;
};

type RelatedSectionProps = {
	renderer: ContentComponentHelper<ArticlePageContent>;
	title: string;
	list: ListContent | undefined;
	mobileOnlyTitle?: boolean;
};

const RelatedSection: FunctionComponent<RelatedSectionProps> = ({ renderer, title, list, mobileOnlyTitle = false }) => (
	<>
		{list && list.items.length > 0 && (
			<section className="bg-theme-white">
				<h4 className={mobileOnlyTitle ? 'dn-ns' : undefined}>{title}</h4>
				{renderer.render(list)}
			</section>
		)}
	</>
);

/**
 * Renders the Article Page
 * queries construct for article data and render the result
 * articles must have published content
 */
export const ArticlePage: FunctionComponent<ArticlePageProps> = ({ articleId }) => {
	const pageName = 'article';
	const dataLayer = {
		page: pageName
	};
	useTrackPageView({ pageName }, dataLayer);
	const { error, loading, data } = useQuery<ArticleDocumentQuery, ArticleDocumentQueryVariables>(ARTICLE_PAGE, {
		variables: { articleId }
	});
	const { pathname } = useRouterLocation();

	useDyRecommendationContext({ type: DyRecommendationContextType.OTHER_PAGE });

	if (loading) {
		return <PageLoading />;
	}

	if (error) {
		return <FiveHundred />;
	}

	if (!data?.articleDocument) {
		return <FourOhFour />;
	}

	const pageContent = new ContentComponentHelper<ArticlePageContent>(data.articleDocument);

	const {
		title: articleTitle,
		subtitle,
		slug,
		seo,
		article_sections,
		related_articles,
		related_categories,
		related_products,
		related_videos,
		author,
		hero_media
	} = pageContent.content.fields;
	const breadcrumbData = [{ name: 'Learning Center', url: LEARNING_CENTER_LINK }, { name: articleTitle }];

	const { title, description } = getSeoMetaData(seo);
	const titleSlug = convertToSlug(title || articleTitle);
	const canonicalURL = `/${slug || titleSlug}/${ARTICLE_PREFIX}${articleId}`;

	if (pathname !== canonicalURL) {
		return <RedirectWithStatus status={301} to={canonicalURL} />;
	}

	return (
		<PageContainer<ArticlePageType>
			canonicalURL={canonicalURL}
			pageTitle={title}
			metaDescription={description}
			breadcrumbs={breadcrumbData}
			showUpdateLocation={true}
			headline={articleTitle}
			description={subtitle}
			imagePublicId={getArticleHeroImageId(hero_media.fields)}
			author={getArticleAuthorName(author.fields)}
			url={SITE_BASE_URL + canonicalURL}>
			<ContentContainer contentId={pageContent.contentId}>
				<div className="flex flex-column flex-row-reverse-l">
					<div className="w-75-l ph2">
						<h1 className="mt2">{articleTitle}</h1>
						<h2 className="i f4">{subtitle}</h2>
						{pageContent.render(author)}
						{pageContent.render(hero_media)}
						{pageContent.render(article_sections)}
						<RelatedSection title="Related Categories" list={related_categories} renderer={pageContent} />
						<RelatedSection title="Related Articles" list={related_articles} renderer={pageContent} />
						<RelatedSection title="Related Videos" list={related_videos} renderer={pageContent} />
					</div>
					<div className="w-25-l">
						<RelatedSection title="Shop Products" list={related_products} renderer={pageContent} mobileOnlyTitle={true} />
					</div>
				</div>
			</ContentContainer>
		</PageContainer>
	);
};
